import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Switch,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { referenceAPI, geocodeAPI } from '../services/api';
import AddReferenceModal from './AddReferenceModal';
import { toArray } from '../utils/apiData';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import useFieldErrors from '../utils/useFieldErrors';

const AddExpenseLocationModal = ({ open, onClose, onSave, onSuccess, apiCall, initialLocationName, initialLocation }) => {
  const notify = useNotify();
  const fields = useFieldErrors();
  const [nameId, setNameId] = useState('');
  const [location, setLocation] = useState('');
  const [coordinates, setCoordinates] = useState('');
  const [online, setOnline] = useState(false);
  const [active, setActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingNames, setLoadingNames] = useState(false);
  const [names, setNames] = useState([]);
  const [openNameModal, setOpenNameModal] = useState(false);
  const [geocoding, setGeocoding] = useState(false);

  // A name can arrive by picking it, by auto-match, or from the nested "+" create;
  // any of them satisfies the required check.
  useEffect(() => {
    if (nameId) fields.clear('name');
  }, [nameId, fields]);

  const loadNames = useCallback(async () => {
    setLoadingNames(true);
    try {
      const response = await referenceAPI.getExpenseLocationNames();
      const namesData = toArray(response.data, 'expense location names');

      // Sort names by sort_order, then by name
      const sorted = [...namesData].sort((a, b) => {
        if (a.sort_order !== b.sort_order) {
          return (a.sort_order || 0) - (b.sort_order || 0);
        }
        return a.name.localeCompare(b.name);
      });

      setNames(sorted);
      return sorted;
    } catch (error) {
      console.error('Error loading location names:', error);
      notify.error(`Error loading location names: ${errorMessage(error, error.message)}`);
      return [];
    } finally {
      setLoadingNames(false);
    }
  }, [notify]);

  useEffect(() => {
    if (open) {
      (async () => {
        const loadedNames = await loadNames();
        if (initialLocationName) {
          const match = loadedNames.find(
            (n) => n.name.trim().toLowerCase() === initialLocationName.trim().toLowerCase()
          );
          if (match) {
            setNameId(String(match.id));
          } else {
            setOpenNameModal(true);
          }
        }
        if (initialLocation) {
          setLocation(initialLocation);
        }
      })();
    }
  }, [open, initialLocationName, initialLocation, loadNames]);

  const lookupCoordinates = async () => {
    const selectedName = names.find(n => String(n.id) === String(nameId));
    const queryParts = [selectedName?.name, location].filter(Boolean);
    if (!queryParts.length) return;

    setGeocoding(true);
    try {
      const { data } = await geocodeAPI.lookup(queryParts.join(', '));
      setCoordinates(`${data.lat}, ${data.lng}`);
    } catch (err) {
      notify.error(errorMessage(err, 'Could not reach the geocoding service.'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleClose = () => {
    setNameId('');
    setLocation('');
    setCoordinates('');
    setOnline(false);
    setActive(true);
    setOpenNameModal(false);
    setGeocoding(false);
    fields.reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!fields.validate({ name: !nameId && 'Name is required' })) return;

    setLoading(true);
    try {
      const data = {
        name_id: Number(nameId),
        online,
        active,
      };

      if (!online) {
        if (location) {
          data.location = location;
        }
        if (coordinates) {
          data.coordinates = coordinates;
        }
      }

      // Support both onSave (old style) and apiCall + onSuccess (new style)
      if (onSave) {
        await onSave(data);
        handleClose();
      } else if (apiCall) {
        const response = await apiCall(data);
        if (onSuccess) {
          onSuccess(response.data);
        }
        handleClose();
      }
    } catch (error) {
      console.error('Error saving:', error);
      notify.error(errorMessage(error, 'Error saving expense location'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm" 
        fullWidth
        sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      >
        <DialogTitle>Add New Expense Location</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <Autocomplete
                fullWidth
                size="small"
                disableClearable
                loading={loadingNames}
                disabled={loadingNames}
                options={names}
                getOptionLabel={(nameItem) => nameItem.name}
                isOptionEqualToValue={(o, v) => String(o.id) === String(v?.id)}
                filterOptions={createPrefixFilter((nameItem) => nameItem.name)}
                noOptionsText="No names available. Click the + button to create one."
                componentsProps={{
                  popper: { disablePortal: true },
                  paper: { sx: { zIndex: (theme) => theme.zIndex.modal + 3 } },
                }}
                value={names.find((nameItem) => String(nameItem.id) === nameId) ?? null}
                onChange={(e, newValue) => setNameId(newValue ? String(newValue.id) : '')}
                renderInput={(params) => <TextField {...params} label="Name" required {...fields.props('name')} />}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={() => setOpenNameModal(true)}
                sx={{ minWidth: 'auto', px: 0.5, height: 40 }}
              >
                <AddIcon fontSize="small" />
              </Button>
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={online}
                  onChange={(e) => setOnline(e.target.checked)}
                />
              }
              label="Online"
            />
            {!online && (
              <>
                <TextField
                  size="small"
                  label="Location (City, State)"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  fullWidth
                  placeholder="e.g., Bend, OR"
                />
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    size="small"
                    label="Coordinates (Latitude, Longitude)"
                    value={coordinates}
                    onChange={(e) => setCoordinates(e.target.value)}
                    fullWidth
                    placeholder="e.g., 44.314202200776265, -120.8694793"
                    helperText="Format: latitude, longitude"
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={lookupCoordinates}
                    disabled={geocoding || (!nameId && !location)}
                    sx={{ minWidth: 'auto', px: 0.5, height: 40, flexShrink: 0 }}
                    title="Look up coordinates"
                  >
                    {geocoding ? <CircularProgress size={16} /> : <MyLocationIcon fontSize="small" />}
                  </Button>
                </Box>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Adding...' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddReferenceModal
        open={openNameModal}
        onClose={() => setOpenNameModal(false)}
        apiCall={referenceAPI.createExpenseLocationName}
        initialName={initialLocationName || ''}
        onSuccess={async (newName) => {
          await loadNames();
          if (newName && newName.id) {
            setNameId(String(newName.id));
          }
        }}
        title="Expense Location Name"
        label="Location Name"
      />
    </>
  );
};

export default AddExpenseLocationModal;
